import { CSSProperties } from "react";
import { sendError } from "./api/GlobalApi";
import states from "./assets/states.json";
import { Environments, ErrorLevels, GlobalConstants } from "./components/constants/GlobalConstants";
import { FeatureFlags } from "./components/constants/LocalServerConstants";
import { IErrorResponse } from "./components/interfaces/Global";
import { getIsWhiteLabeled } from "./helpers/PermissionHelper";
import { PayloadContentType } from "./components/lms/ui/LmsFormGenerator";

export function firstToUpper(value: string) {
    if (value.length < 1) {
        return value;
    }
    else {
        return value.charAt(0).toUpperCase() + value.slice(1);
    }
}

export function guid() { 
  let d = new Date().getTime();
  let d2 = (performance && performance.now && (performance.now()*1000)) || 0;
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function(c) {
      let r = Math.random() * 16;
      if(d > 0){
          r = (d + r)%16 | 0;
          d = Math.floor(d/16);
      } else {
          r = (d2 + r)%16 | 0;
          d2 = Math.floor(d2/16);
      }
      return (c === "x" ? r : ((r & 0x3) | 0x8)).toString(16);
  });
}

export function getStateOptions() {
    const stateOptions = [] as any;
    states.forEach((state) => {
      stateOptions.push({
        key: state.abbreviation,
        text: state.abbreviation,
        value: state.abbreviation
      });
    });
    return stateOptions;
}

export function getStateFullOptions() {
  const stateOptions = [] as any;
  states.forEach((state) => {
    stateOptions.push({
      key: state.abbreviation,
      text: state.abbreviation,
      value: state.name
    });
  });
  return stateOptions;
}

export function getDropdownOptions(data, props = undefined as any, text = undefined as any) {
  const dropdownOptions = [] as any;
  if (data) {
    data.forEach((item) => {
      const options = [] as any;
      let optionValue = "";
      if (props === undefined) {
        optionValue = item;
      }
      else {
        props.forEach((prop) => {
          options.push(item[prop]);
        });
        optionValue = options.join(" ");
      }
  
      let textValue = optionValue;
      if (text) {
        textValue = item[text];
      }
  
      dropdownOptions.push({
        key: optionValue,
        text: textValue,
        value: optionValue
      });
    });
  }
  
  return dropdownOptions;
}

export function roundNumber(num) {
  return Math.round(((num * 1) + Number.EPSILON) * 100) / 100;
}

export function convertToDollar(num) {
  if (num || num === 0) {
    return "$" + roundNumber(num).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2});
  }
  
  return num;
}

export function convertToPercentString(num, minToFixed = 3, maxToFixed = 3) {
  if (num) {
    return (roundNumber(num * 100)).toLocaleString("en", { minimumFractionDigits: minToFixed, maximumFractionDigits: maxToFixed }) + "%";
  }
  
  return num;
}

export function convertToPercent(num, minToFixed = 3, maxToFixed = 3) {
  if (num) {
    return (roundNumber(num * 100)).toLocaleString("en", { minimumFractionDigits: minToFixed, maximumFractionDigits: maxToFixed });
  }
  
  return num;
}

export function convertToMinFraction(num, toFixed = 2, maxToFixed = 2, zeroToFixed = false) {
  if (num || (num === 0 && zeroToFixed)) {
    return roundNumber(num).toLocaleString("en", { minimumFractionDigits: toFixed, maximumFractionDigits: maxToFixed });
  }
  
  return num;
}

export function convertToDateString(date, option = "") {
  if (option === "full" && date) {
    return new Date(date).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  }
  else if (option === "calendar" && date) {
    const convertedDate = new Date(date);
    const twoDigitMonth = (convertedDate.getMonth() + 1).toLocaleString("en-US", { minimumIntegerDigits: 2, useGrouping: false });
    const twoDigitDay = convertedDate.getDate().toLocaleString("en-US", { minimumIntegerDigits: 2, useGrouping: false });
    return `${convertedDate.getFullYear()}-${twoDigitMonth}-${twoDigitDay}`;
  }
  else if (option === "datetimewithoutsecond" && date) {
    return `${new Date(date).toLocaleDateString()} ${new Date(date).toLocaleTimeString(navigator.language, {hour: "2-digit", minute:"2-digit"})}`;
  }
  else if (option === "datefullformatted" && date) {
    const options = { year: "numeric", month: "short", day: "numeric" } as any;
    return `${new Date(date).toLocaleString("en-US", options)} ${new Date(date).toLocaleTimeString(navigator.language, { hour: "2-digit", minute: "2-digit" })}`;
  }
  else if (option === "date" && date) {
    return `${new Date(date).toLocaleDateString()}`;
  }
  else if (option === "lmsdisplaydateforfieldname" && date) {
    return `${new Date(date).toLocaleString("en-US", { dateStyle: "short" })}`;
  }
  else if (option !== "full" && date) {
    return new Date(date).toLocaleDateString();
  }

  return date;
}

export function convertToYesNo(bool) {
  if (bool) {
    return "Yes";
  }
  else {
    return "No";
  }
}

export function convertStateToAbbreviation(stateName) {
  return states.find(x => x.name === stateName)?.abbreviation;
}

export function convertToEINString(ein) {
  if (ein) {
    return `${ein.substring(0, 3)}-${ein.substring(3, 6)}-${ein.substring(6, 9)}`;
  }
  
  return ein;
}

export function converToSSNString(ssn) {
  if (ssn) {
    const lastFourSSN = ssn.substring(5, 9);
    return `xxx-xx-${lastFourSSN}`;
  }
  
  return ssn;
}

export function convertToOrganizationString(organization) {
  if (organization) {
    return organization.replace(/\[\d+\]$/, "");
  }
  else {
    return organization;
  }
}

export function converToStartDateToEndDateString(startDate, endDate) {
  if (startDate && endDate) {
    return `${convertToDateString(startDate, "lmsdisplaydateforfieldname")}-${convertToDateString(endDate, "lmsdisplaydateforfieldname")}`;
  }

  return null;
}

export function operationOfNumberArray(operation, numArray) {
  if (numArray && numArray.length > 1) {
    if (operation === "sum") {
      let sum = 0 as number;
      numArray.forEach(num => {
        if (Number(num)) {
          sum += Number(num);
        }
      });
  
      return sum;
    }
    else if (operation === "difference") {
      let sum = 0 as number;
      let firstValue = undefined as number | undefined;
      numArray.forEach(num => {
        if (num && firstValue) {
          sum += num;
        }
        else if (num) {
          firstValue = num;
        }
      });
  
      if (firstValue) {
        return firstValue - sum;
      }
      else {
        return null;
      }
    }
    else if (operation === "product") {
      let product = 1 as number;
      numArray.forEach(num => {
        if (num) {
          product *= num;
        }
      });
  
      return product;
    }
  }
  else {
    return null;
  }
}

export function formatter(num, type = "M", toFixed = 1) {
  let formatNum = 1000000;
  let formatLabel = "M";
  switch(type) {
    case "K":
      formatNum = 1000;
      formatLabel = "K";
      break;
    default:
      break;
  }
  return Math.abs(num) > 999 ? Math.sign(num) * parseFloat((Math.abs(num) / formatNum).toFixed(toFixed)) + formatLabel : Math.sign(num) * Math.abs(num);
}

export function calculateArrTotal(arr, prop) {
  let total = 0;
  arr.forEach(item => {
    total += item[prop];
  });
  return total;
}

export function calculateDateDifference(before, target = "today") {
  let targetDate = new Date();
  if (target !== "today") {
    targetDate = new Date(target);
  }
  const beforeDate = new Date(before);
  const timeDiff = targetDate.getTime() - beforeDate.getTime(); 
  const dayDiff = timeDiff / (1000 * 3600 * 24);
  return Math.round(dayDiff);
}

export function printDaysLeft(num) {
  switch(num) {
    case 0:
      return "Today";
    case 1:
      return "Yesterday";
    default:
      return num + " days ago";
  }
}

export function printDaysToGo(num) {
  const days = num * -1;
  switch(days) {
    case 0:
      return "Today";
    case 1:
      return "Yesterday";
    default:
      return days + " days to go";
  }
}

export function dynamicSort(property) {
  let sortOrder = 1;
  if(property[0] === "-") {
      sortOrder = -1;
      property = property.substr(1);
  }
  return function (a,b) {
      const result = (a[property] < b[property]) ? -1 : (a[property] > b[property]) ? 1 : 0;
      return result * sortOrder;
  };
}

export function dynamicSortMultiple(...args: any[]) {
  const props = args;
  return function (obj1, obj2) {
      let i = 0;
      let result = 0; 
      const numberOfProperties = props.length;
      while(result === 0 && i < numberOfProperties) {
          result = dynamicSort(props[i])(obj1, obj2);
          i++;
      }
      return result;
  };
}

export function isWhiteLabeled (permissions) {
  if (getIsWhiteLabeled() && (isValidPermission(permissions, "GuarantorWhiteLabeledApplication", "read") || isValidPermission(permissions, "BorrowerWhiteLabeledApplication", "read"))) {
    return true;
  }
  else {
    return false;
  }
}

export function isGuid (guid) {
  const matches = new RegExp(/^[{]?[0-9a-fA-F]{8}-([0-9a-fA-F]{4}-){3}[0-9a-fA-F]{12}[}]?$/);
  return matches.test(guid);
}

export function validateObj(obj, items) {
  const invalidItems = [] as any;
  if (items) {
    items.forEach(item => {
      if (!(obj && obj[item] && obj[item] !== "" && obj[item]) && obj[item] !== false && obj[item] !== 0) {
        invalidItems.push(item);
      }
    });
  }
  return invalidItems;
}

export function validateNestedObj(array, arrayName, validatingProps) {
  const invalidItems = [] as any;
  if (array) {
    array.forEach((item, i) => {
      validatingProps.forEach((validatingProp) => {
        if (!(item && item[validatingProp] && item[validatingProp] !== "" && item[validatingProp]) && 
          item[validatingProp] !== false && item[validatingProp] !== 0) {
            invalidItems.push(`${arrayName}-${validatingProp}-${i}`);
          }
      });
    });
  }

  return invalidItems;
}

export function validateEmail(email) 
{
  if (/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(email)) {
    return true;
  }
  else {
    return false;
  }
}

export function validateEmails(obj, items) {
  const invalidItems = [] as any;
  if (items) {
    items.forEach(item => {
      if (!(obj && obj[item] && validateEmail(obj[item]))) {
        invalidItems.push(item);
      }
    });
  }
  return invalidItems;
}

export function uniqueArray(arr) {
  return arr.filter(function(item, pos){
    return arr.indexOf(item) === pos; 
  });
}

function replaceErrors(key, value) {
  if (value instanceof Error) {
      const error = {};
      Object.getOwnPropertyNames(value).forEach(function (key) {
          error[key] = value[key];
          
        });
      return undefined;
  }
  return value;
}

export function createExceptionMessage(trace, clientData = "", statusCode = undefined, operation = "") {
  const error = new Error(trace);
  const exceptionMessage = {
    clientData: clientData === "" ? "" : clientData,
    callStack: JSON.stringify(error, replaceErrors),
    statusCode: statusCode && statusCode,
    operation: operation === "" ? "" : operation,
    time: new Date()
  };
  const strExceptionMessage = JSON.stringify(exceptionMessage);

 return strExceptionMessage;
}

export async function createErrorResponse(errorType, errorMessage, data = undefined, endPoint = "", errorNo = -1, statusCode = undefined, operation = "", obj = {}) {
  if (FeatureFlags.IS_DEBUG) {
    Object.keys(obj).length === 0 ? console.log(obj) : console.log(errorMessage);
  }
  if (!FeatureFlags.IS_MOCK) {
    logErrorToApi(errorType, errorMessage, data, endPoint, errorNo, statusCode, operation);
  }
}

export async function logErrorToApi (errorType, errorMessage, data = undefined, endPoint = "", errorNo = -1, statusCode = undefined, operation = "") {
  try{
    const errorResponse = {
      errorNo: errorNo === -1 ? null : errorNo,
      errorType: errorType, 
      time: new Date(),
      endPoint: endPoint === "" ? null : endPoint,
      errorMessage: errorMessage,
      exception: createExceptionMessage(errorMessage, data && data, statusCode && statusCode, operation === "" ? undefined : operation)
    } as any;
    if (statusCode){
      FeatureFlags.IS_DEBUG && console.log(errorResponse);
    }
    else {
      const response = await sendError(errorResponse);
      if (response && response.ok) {
        FeatureFlags.IS_DEBUG && console.log(errorResponse);
      } else {
        FeatureFlags.IS_DEBUG && console.log(response);
      } 
    }
  }
  catch (error) {
    FeatureFlags.IS_DEBUG && console.log(error);
  }
}

export function isValidPermission(permissionsSet, permissionName, permissionType) {
  const foundPermission = permissionsSet.find(x => x.name === permissionName);

  if(foundPermission) {
    switch(permissionType) {
      case "read":
        if (foundPermission.read === true) {
          return true;
        }
        break;
      case "create":
        if (foundPermission.create === true) {
          return true;
        }
        break;
      case "edit":
        if (foundPermission.edit === true) {
          return true;
        }
        break;
      case "update":
        if (foundPermission.update === true) {
          return true;
        }
        break;
      case "delete":
        if (foundPermission.delete === true) {
          return true;
        }
        break;
      case "export":
        if (foundPermission.export === true) {
          return true;
        }
        break;
    }
  }
  else {
    return false;
  }
  return false;
}

export function isValidDashboard(data, dataType, nestedProp = "id", isObject = false) {
  if (data && Object.keys(data).includes(dataType) && data[dataType]) {
    if (isObject === false && data[dataType].length > 0) {
      let isValidNestedObject = true;
      data[dataType].forEach(nestedObj => {
        if (nestedObj && !nestedObj[nestedProp]) {
          FeatureFlags.IS_DEBUG && console.log(nestedObj);
          !FeatureFlags.IS_MOCK&& createErrorResponse("Error", "Error in NestedObj of isValidDashboard", nestedObj);
          isValidNestedObject = false;
        }
      });
      return isValidNestedObject;
    }
    else if (isObject === true && Object.keys(data[dataType]).length > 0) {
      return true;
    }
  }
  return false;
}

export function convertCamelCase(value) {
  const result = value?.replace( /([A-Z])/g, " $1" );
  const finalResult = result.charAt(0).toUpperCase() + result.slice(1);
  return finalResult;
}

export function formatDataProperties(data, customRules = [] as any[]) {
  const formattedData = [] as any;
  data && data.forEach(item => {
    const formattedObj = {} as any;
    for (const [key, value] of Object.entries(item)) {
      if (customRules.length !== 0 && Array.from(customRules, x => x.field).includes(key)) {
        const rule = customRules.find(x => x.field === key);
        switch(rule.action) {
          case "remove":
            break;
          case "rename":
            formattedObj[rule.name] = value;
            break;
          default:
            FeatureFlags.IS_DEBUG && console.log("action not found");
            !FeatureFlags.IS_MOCK  && createErrorResponse("Warning", "Action Not Found");
            break;
        }
      }
      else {
        formattedObj[convertCamelCase(key)] = value;
      }
    }
    formattedData.push(formattedObj);
  });

  return formattedData;
}

export async function createFile(data, name, mimetype){
  const metadata = {
    type: mimetype
  };
  const file = new File([data], name, metadata);
  return file;
}

export function APIHeader(proxyUserId, authToken, contentType: PayloadContentType = "JSON") {
  const headers = {
    "Access-Control-Allow-Origin": "*",
    "Authorization": `Bearer ${authToken}`
  };

  if (contentType === "JSON") {
    headers["Accept"] = "application/json";
    headers["Content-Type"] = "application/json";
  }
  
  if (proxyUserId) {
    headers["impersonatedUserId"] = proxyUserId;
  }

  return headers;
}

export function hasDuplicates(array) {
  return (new Set(array)).size !== array.length;
}

export function hasDuplicatesArray(arr1, arr2) {
  return ((arr1.filter(element => arr2.includes(element))).length !== 0);
}

export const handleGetValueFromId = (array, id, prop = "name") => {
  if (array) {
    return array.find(x => x.id === id) && array.find(x => x.id === id)[prop];
  }
  else {
    return null;
  }
};

export const handleGetIdFromValue = (array, value, prop = "name") => {
  if (array) {
    return array.find(x => x[prop] === value) && array.find(x => x[prop] === value).id;
  }
  else {
    return null;
  }
};

const handleFloatInput = (value, max = undefined as number | undefined) => {
  if (value) {
    const parsedValue = value.replace(/[^\d.]/gi, "");
    if (parsedValue.charAt(parsedValue.length - 1) === ".") {
      if (max && (parsedValue > max)) {
        return max;
      }
      
      return parsedValue;
    }

    if (!isNaN(parseFloat(value))) {
      if (max && (value > max)) {
        return max;
      }

      return parsedValue;
    }
    else if (value === "") {
      return undefined;
    }

    return parsedValue;
  }
  
  return value;
};

export const handleInputType = (type, value, formObj = undefined as any, field = "", charLimit = undefined as number | undefined) => {
  if (type === GlobalConstants.InputTypes.SOCIAL_SECURITY) {
    type = GlobalConstants.InputTypes.MASKED;
  }
  
  if (type === GlobalConstants.InputTypes.INT) {
    if (charLimit && value && value.length > charLimit) {
      return value.substring(0, value.length - 1);
    }
    
    if (value) {
      value = value.toString().replace(/[,\s]+|[,\s]+/g, "");
      return parseInt(value);
    }
    
    return value;
  }
  else if (type === GlobalConstants.InputTypes.FLOAT || type === GlobalConstants.InputTypes.DOLLAR) {
    if (value) {
      const parsedValue = value.toString().replace(/[^\d.]/gi, "");
      if (parsedValue.charAt(parsedValue.length - 1) === ".") {
        return parsedValue;
      }

      if (!isNaN(parsedValue)) {
        return parsedValue;
      }
    }
    
    return value;
  }
  else if (type === GlobalConstants.InputTypes.STRING) {
    if (charLimit && value && value.length > charLimit) {
      return value.substring(0, value.length - 1);
    }

    return value;
  }
  else if (type === GlobalConstants.InputTypes.ZIP) {
    const replacedValue = value.replace(/[^0-9]+/g, "");
    if (value.length <= 5) {
      return replacedValue;
    }
    else if (!isNaN(value) && !value.includes("e")) {
      return value.substring(0, value.length - 1);
    }

    return formObj[field];
  }
  else if (type === GlobalConstants.InputTypes.PROPERTIES) {
    if (value <= 100) {
      return parseInt(value);
    }
    
    return 100;
  }
  else if (type === GlobalConstants.InputTypes.CREDIT) {
    if (!isNaN(parseInt(value))) {
      if (value > 850) {
        return 850;
      }

      return parseInt(value);
    }
    else if (value === "") {
      return undefined;
    }
  }
  else if (type === GlobalConstants.InputTypes.WL_BROKER_FEE) {
    return handleFloatInput(value, 1.5);
  }
  else if (type === GlobalConstants.InputTypes.PERCENTAGE) {
    return handleFloatInput(value, 100);
  }
  else if (type === GlobalConstants.InputTypes.MASKED) {
    if (charLimit && value && value.length > charLimit) {
      return formObj[field];
    }

    return value.replace(/\D/g, "");
  }
  else if (type === GlobalConstants.InputTypes.ORGANIZATION) {
    if (charLimit && value && value.length > charLimit) {
      return formObj[field];
    }
    
    return value.replace(/\[\d+\]$/, "");
  }
  else if (type === GlobalConstants.InputTypes.LOAN_TERM) {
    if (value) {
      value = value.toString().replace(/[,\s]+|[,\s]+/g, "");
      if (parseInt(value) > 12) {
        return 12;
      }

      return parseInt(value);
    }
    
    return value;
  }
  else if (type === GlobalConstants.InputTypes.EIN) {
    const replacedValue = value.replace(/[^0-9]+/g, "");
    if (value.length <= 9) {
      return replacedValue;
    }
    else if (!isNaN(value) && !value.includes("e")) {
      return value.substring(0, value.length - 1);
    }

    return formObj[field];
  }
  else if(type === GlobalConstants.InputTypes.SUBDOMAIN) {
    if (charLimit && value && value.length > charLimit) {
      return formObj[field];
    }
    
    return value.replace(/^((?!^[a-zA-Z](-?[0-9a-zA-Z]-?)*$).)*$/i, "");
  }
  else {
    createErrorResponse("Warning", "The form type " + type + " is not supported");
    return value;
  }
};

export const handleFormChange = (value, field, obj, setObj, type = "string", handleCustomFormChange = undefined as any, charLimit = undefined as number | undefined, index = -1 as number, objArray = undefined as any) => {
  const formObj = obj;
  if (handleCustomFormChange) {
    handleCustomFormChange(field, value, formObj);
  }
  
  if (index !== -1 && objArray) {
    formObj[field] = handleInputType(type, value, formObj, field, charLimit);
    objArray[index] = {...formObj};
    setObj([...objArray]);
  }
  else if (index !== -1) {
    formObj[index][field] = handleInputType(type, value, formObj, field, charLimit);
    setObj([...formObj]);
  }
  else {
    formObj[field] = handleInputType(type, value, formObj, field, charLimit);
    setObj({...formObj});
  }
};

export function onlyUnique(value, index, self) {
  return self.indexOf(value) === index;
}

export const removeFromArrayByValues = (removeArray, removeValues) => {
  removeValues.forEach(removeValue => {
    const foundIndex = removeArray.indexOf(removeValue);
    if (foundIndex !== -1) {
      removeArray.splice(foundIndex, 1);
    }
  });

  return removeArray;
};

export const setBackgroundColor = (color: string) => {
  const mystyle: CSSProperties = {
    backgroundColor: `${color}`
  };
  return mystyle;
};

export const setColor = (color: string) => {
  const mystyle: CSSProperties = {
    color: `${color}`
  };
  return mystyle;
};

export const isApplicationGuarantor = (permissionsSet, permissionName, permissionType, guarantors, organizationContactId) => {
  let isGuarantor = false;
  const guarantorsIds = Array.from(guarantors as any[], x => x.id);
  guarantorsIds.forEach(guarantorId => {
    if (organizationContactId === guarantorId && isValidPermission(permissionsSet, permissionName, permissionType)) {
      isGuarantor = true;
    }
  });

  return isGuarantor;
};

export const isValidGuarantorAccessLevel = (permissionsSet, permissionName, permissionType, guarantors, organizationContactId, accessLevelId) => {
  let isGuarantor = false;
  guarantors.forEach(guarantor => {
    if (organizationContactId === guarantor.id && isValidPermission(permissionsSet, permissionName, permissionType) && accessLevelId === guarantor.accessLevelId) {
      isGuarantor = true;
    }
  });

  return isGuarantor;
};

export const isApplicationBorrower = (permissionsSet, permissionName, permissionType, borrowerOrgContactId, organizationContactId) => {
  return getIsWhiteLabeled() && isValidPermission(permissionsSet, permissionName, permissionType) && borrowerOrgContactId === organizationContactId;
};

export const handlePatch = (originalState, patchObj) => {
  Object.keys(patchObj).forEach(prop => {
    if (originalState[prop]) {
      originalState[prop] = patchObj[prop];
    }
  }); 

  return originalState;
};

export const formatPatch = (string, obj) => {
  return {"borrowerApplication" : [obj]};
};

export const handleAddressValue = (addressComponents) => {
  const addressArray = [] as string[];
  const stateZipArray = [] as string[];
  const countryArray = [] as string[];
  Object.entries(addressComponents).forEach(entry => {
    const [key, value] = entry as [string, string];
    if (key === "address" && value) {
      addressArray.push(value);
    }
    else if (key === "city" && value) {
      addressArray.push(value);
    }
    else if (key === "state" && value) {
      stateZipArray.push(value);
    }
    else if (key === "zip" && value) {
      stateZipArray.push(value);
    }
    else if (key === "country" && value) {
      countryArray.push(value);
    }
  });

  if (stateZipArray.length > 0) {
    const stateZip = stateZipArray.join(" ");
    addressArray.push(stateZip);
  }

  if (countryArray[0]) {
    addressArray.push(countryArray[0]);
  }

  return addressArray.join(", ");
};

export const deepCopy = (data : any[] | any) => {
  return JSON.parse(JSON.stringify(data));
};

export const parseAddressLine1 = (fullAddress: string) => {
  const firstAddressSplitIndex = fullAddress.indexOf(" - ");
  if (firstAddressSplitIndex > -1) {
    return fullAddress.substring(0, firstAddressSplitIndex);
  }
  else {
    return fullAddress;
  }
};

export const parseAddressLine2 = (fullAddress: string) => {
  const firstAddressSplitIndex = fullAddress.indexOf(" - ");
  if (firstAddressSplitIndex > -1) {
    const lastAddressSplitIndex = firstAddressSplitIndex + 3;
    return fullAddress.substring(lastAddressSplitIndex, fullAddress.length);
  }
  else {
    return "";
  }
};

export const combineAddresses = (addressLine1: string, addressLine2: string) => {
  return `${addressLine1}${addressLine2 ? ` - ${addressLine2}` : ""}`;
};

export const getErrorColor = (errorLevel: string) => {
  if (errorLevel.toLowerCase() === GlobalConstants.ErrorLevels.INFO.toLowerCase()) {
    return "blue";
  }
  else if (errorLevel.toLowerCase() === GlobalConstants.ErrorLevels.WARNING.toLowerCase()) {
    return "yellow";
  }

  return "red";
};

export const getErrorCode = (id: string) => {
  return id.substring(id.length - 6, id.length);
};

export const getErrorMessage = (errorId: string | undefined, errorMessage) => {
  return `${errorMessage}${errorId ? `\nError Code: ${getErrorCode(errorId).toUpperCase()}` : ""}`;
};

export const handleSpaFwEventLog = (message, setIsMessage, setErrorResponse) => {
  createErrorResponse("Error", message);
  setIsMessage(true);
  setErrorResponse({
    errorId: guid(),
    errorTitle: "Oops! It looks like something went wrong.",
    errorMessage: "Please contact your support representative."
  });
};

export const handleErrorResponse = async (response, setIsMessage, setErrorResponse) => {
  const errorResponse = await response.json() as IErrorResponse;
  if (errorResponse && errorResponse.errorId) {
    setIsMessage(true);
    if ([Environments.PRODUCTION, Environments.PREPROD].indexOf(process.env.REACT_APP_ENV ?? "") > -1 && errorResponse.errorLevel === ErrorLevels.ERROR) {
      errorResponse.errorTitle = "Oops! It looks like something went wrong.";
      errorResponse.errorMessage = "Please contact your support representative.";
    }
    else if ([Environments.PRODUCTION, Environments.PREPROD].indexOf(process.env.REACT_APP_ENV ?? "") === -1) {
      if (errorResponse.errorLevel === ErrorLevels.ERROR) {
        errorResponse.errorTitle = errorResponse.errorTitle + " System Error.";
      }
      else if (errorResponse.errorLevel === ErrorLevels.USER_ERROR) {
        errorResponse.errorTitle = errorResponse.errorTitle + " User Error.";
      }
    }

    setErrorResponse(errorResponse);
  }
  else {
    setIsMessage(true);
    setErrorResponse({ errorId: guid(), errorTitle: "Oops!", errorMessage: "It looks like something went wrong."} as IErrorResponse);
  }

  return errorResponse;
};

export const formatLeadingNumber = (number, leadAmount) => {
  const convertedNumber = parseInt(number);
  if (!isNaN(convertedNumber)) {
    return (convertedNumber).toPrecision(leadAmount).split(".").reverse().join("");
  }
  
  return null;
};

export const convertHexToRgb = (hex: string) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : {
    r: 0,
    g: 0,
    b: 0
  };
};

export const filterOutCollateral = (properties) => {
  const filteredProperties = [] as any[];
  properties.forEach(property => {
    if (property.collateralProperty === false && property.loanPropertyUnitReconveyances.length > 0) {
      filteredProperties.push(property);
    }
  });

  return filteredProperties;
};

export const handleNestedValidation = (data, props, group, errorItemIdSet?: Set<string> | null) => {
  const invalidProps = [] as string[];
  if (data) {
    data.forEach((item) => {
      props.forEach((prop) => {
        if (!(item && item[prop] && item[prop] !== "" && item[prop]) && item[prop] !== false && item[prop] !== 0) {
          invalidProps.push(`${group}-${item.id}-${prop}`);

          if (errorItemIdSet && !errorItemIdSet.has(item.id)) {
            errorItemIdSet.add(item.id);
          }
        }
      });
    });
  }

  return invalidProps;
};

export const isNumber = (value: unknown): boolean =>
  typeof value === "number" && Number.isFinite(value);
